import { useRouter } from "@core/Api/useRouter";
import {
	applyOverrides,
	getCatalogOverrides,
	reconcileDroppedNodes,
	setCategoryOverride,
	syncOverrides,
} from "@core/SitePresenter/NavTreeStateManager";
import MimeTypes from "@core-ui/ApiServices/Types/MimeTypes";
import ArticlePropsService from "@core-ui/ContextServices/ArticleProps";
import ModalToOpenService from "@core-ui/ContextServices/ModalToOpenService/ModalToOpenService";
import ModalToOpen from "@core-ui/ContextServices/ModalToOpenService/model/ModalsToOpen";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import { useDeferApi } from "@core-ui/hooks/useApi";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import type OtherLanguagesPresentWarning from "@ext/localization/actions/OtherLanguagesPresentWarning";
import { shouldShowActionWarning } from "@ext/localization/actions/OtherLanguagesPresentWarning";
import Localizer from "@ext/localization/core/Localizer";
import type { NodeModel } from "@minoru/react-dnd-treeview";
import { type ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ItemLink } from "../../../NavigationLinks";
import DragTreeTransformer from "../logic/DragTreeTransformer";
import LevNavDragTree from "./LevNavDragTree";

type handleOnDropType = (
	draggedItemPath: string,
	newTree: NodeModel<ItemLink>[],
	fetchComplete: () => void,
) => Promise<void> | void;

const ExportLevNavDragTree = ({ items, closeNavigation }: { items: ItemLink[]; closeNavigation?: () => void }) => {
	const isReadOnly = PageDataContextService.value.conf.isReadOnly;
	const articleProps = ArticlePropsService.value;
	const supportedLanguages = useCatalogPropsStore((state) => state.data?.supportedLanguages, "shallow");
	const catalogName = useCatalogPropsStore((state) => state.data?.name);
	const [dragged, setDragged] = useState<boolean>(!isReadOnly);
	const [treeData, setTreeData] = useState<NodeModel<ItemLink>[]>([]);
	const router = useRouter();

	const treeDataRef = useRef(treeData);
	const itemsRef = useRef(items);
	const articlePropsRef = useRef(articleProps);
	const routerRef = useRef(router);

	const { call: updateCatalogNav } = useDeferApi<NodeModel<ItemLink>[]>({ opts: { mime: MimeTypes.json } });

	// biome-ignore lint/correctness/useExhaustiveDependencies: it's ok
	useEffect(() => {
		treeDataRef.current = treeData;
		itemsRef.current = items;
		articlePropsRef.current = articleProps;
		routerRef.current = router;
	}, [treeData, items, articleProps, router]);

	useEffect(() => {
		syncOverrides(catalogName, items, supportedLanguages);
		setTreeData(
			DragTreeTransformer.getRenderDragNav(
				applyOverrides(items, getCatalogOverrides(catalogName), supportedLanguages),
			),
		);
	}, [items, catalogName, supportedLanguages]);

	const languageKey = useMemo(() => {
		const langs = supportedLanguages ?? [];
		for (const node of treeData) {
			const language = Localizer.languageOf(node.data?.ref?.path ?? "", langs);
			if (language) return language;
		}
		return "";
	}, [treeData, supportedLanguages]);

	const handleOnDrop: handleOnDropType = useCallback(
		async (draggedItemPath, newTree, fetchComplete) => {
			if (!DragTreeTransformer.isModified(draggedItemPath, treeDataRef.current, newTree)) return;
			setDragged(false);

			const newItems = await updateCatalogNav({
				url: (api) => api.updateCatalogNav(articlePropsRef.current.ref.path),
				opts: {
					body: { draggedItemPath, old: treeDataRef.current, new: newTree },
					parse: "json",
					mime: MimeTypes.json,
				},
			});
			if (!newItems) return;

			fetchComplete();
			const currentItem = newItems.find((i) => i.data.isCurrentLink);
			if (currentItem) routerRef.current.pushPath(currentItem.data.pathname);

			setTreeData(
				reconcileDroppedNodes(catalogName, treeDataRef.current, newItems, draggedItemPath, supportedLanguages),
			);
			setDragged(true);
		},
		[updateCatalogNav, catalogName, supportedLanguages],
	);

	const onDrop = useCallback(
		(...args: [draggedItemPath: string, newTree: NodeModel<ItemLink>[], fetchComplete: () => void]) => {
			shouldShowActionWarning(supportedLanguages?.length)
				? ModalToOpenService.setValue<ComponentProps<typeof OtherLanguagesPresentWarning>>(
						ModalToOpen.MultilangActionConfirm,
						{
							action: () => handleOnDrop(...args),
							isOpen: true,
							onClose: () => ModalToOpenService.resetValue(),
						},
					)
				: handleOnDrop(...args);
		},
		[supportedLanguages?.length, handleOnDrop],
	);

	const handleOpenChange = useCallback(
		(id: number | string, open: boolean, nodes: NodeModel<ItemLink>[]) => {
			const path = nodes.find((n) => n.id === id)?.data?.ref?.path;
			if (path) setCategoryOverride(catalogName, itemsRef.current, path, open, supportedLanguages);
		},
		[catalogName, supportedLanguages],
	);

	useEffect(() => {
		setDragged(!isReadOnly);
	}, [isReadOnly]);

	return (
		<LevNavDragTree
			canDrag={dragged}
			closeNavigation={closeNavigation}
			items={treeData}
			key={languageKey}
			onDrop={onDrop}
			onOpenChange={handleOpenChange}
		/>
	);
};

export default ExportLevNavDragTree;
